/**
 * `SocketProvider` — Wave 2.38 FE (TASK-106a).
 *
 * Owns a single `socket.io-client` instance per authenticated session and
 * publishes it through `<SocketContext>`. Lifecycle:
 *   1. Provider mounts — context is `{ socket: null, status: 'idle' }`.
 *   2. As soon as `useAuth().accessToken` is non-null, the provider creates a
 *      socket bound to that token and calls `socket.connect()`.
 *   3. When the token changes (single-flight refresh in `api-client.ts`) the
 *      old socket is disconnected and a fresh one is created. Recreating is
 *      simpler than swapping `socket.auth + socket.connect()` and avoids
 *      stale-closure surprises inside the socket.io internals.
 *   4. On logout (`accessToken` becomes `null`) the socket disconnects and
 *      the context returns to idle.
 *
 * Reconnection is handled by socket.io itself (5 attempts, exponential
 * backoff). If those attempts also fail, the FE keeps the existing 30s / 60s
 * HTTP polling in `NotificationCenterDrawer` / `Topbar` so the inbox does
 * not stall.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuth } from '@/lib/auth-provider';
import {
  resolveSocketUrl,
  SocketContext,
  type SocketConnectionStatus,
} from '@/lib/use-socket';

export function SocketProvider({ children }: { children: ReactNode }) {
  const { accessToken, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<SocketConnectionStatus>('idle');
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      // Clean up any leftover socket on logout / before bootstrap completes.
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setStatus('idle');
      return;
    }

    const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
    const url = resolveSocketUrl(baseUrl);
    const socket = io(url, {
      auth: { token: accessToken },
      transports: ['websocket'],
      autoConnect: false,
      withCredentials: true,
    });

    socketRef.current = socket;
    setStatus('connecting');

    const handleConnect = () => setStatus('connected');
    const handleDisconnect = () => setStatus('disconnected');
    const handleConnectError = () => setStatus('disconnected');

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    socket.connect();

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.removeAllListeners();
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [accessToken, isAuthenticated]);

  const value = useMemo(
    () => ({ socket: socketRef.current, status }),
    // socketRef.current is intentionally part of the dep list via `status`:
    // every status transition above also corresponds to a socket lifecycle
    // change, so consumers receive a fresh context object at the same time.
    [status],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
